import React, { useState, useEffect } from "react";
import {
  PieChart, Pie, Cell, ComposedChart, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { formatCurrency, formatMonthsToYearsMonths } from "../utils/formatters";
import { CHART_COLORS } from "../constants/defaults";

const tooltipStyle = {
  borderRadius: 10,
  border: "1px solid var(--border)",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  fontSize: 13,
};

const axisStyle = { fontSize: 11, fill: "var(--gray-400)" };

const ResultRow = ({ icon, label, value, color }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--gray-100)" }}>
    <span style={{ fontSize: 13, color: "var(--gray-500)", display: "flex", alignItems: "center", gap: 6 }}>
      {icon} {label}
    </span>
    <span style={{ fontSize: 15, fontWeight: 800, color, letterSpacing: "-0.01em" }}>{value}</span>
  </div>
);

const SIPTab = ({ sipState }) => {
  const {
    sipTarget, setSipTarget,
    sipPeriod, setSipPeriod,
    sipReturnRate, setSipReturnRate,
    sipAmount, setSipAmount,
    sipCalculation,
  } = sipState;

  const [sipTargetStr, setSipTargetStr] = useState(String(sipTarget));
  const [sipAmountStr, setSipAmountStr] = useState(String(sipAmount));
  const [sipPeriodStr, setSipPeriodStr] = useState(String(sipPeriod));
  const [sipReturnRateStr, setSipReturnRateStr] = useState(String(sipReturnRate));

  // Sync string state when numeric state changes externally (e.g. from copyLoanToSip)
  useEffect(() => { if (document.activeElement?.id !== "sipTarget") setSipTargetStr(String(sipTarget)); }, [sipTarget]);
  useEffect(() => { if (document.activeElement?.id !== "sipAmount") setSipAmountStr(String(sipAmount)); }, [sipAmount]);
  useEffect(() => { if (document.activeElement?.id !== "sipPeriod") setSipPeriodStr(String(sipPeriod)); }, [sipPeriod]);
  useEffect(() => { if (document.activeElement?.id !== "sipReturnRate") setSipReturnRateStr(String(sipReturnRate)); }, [sipReturnRate]);

  const isFromLoan = sipCalculation.calculationType && sipCalculation.targetAmount > 0;

  const comparisonData = [
    {
      name:
        sipCalculation.calculationType === "total" && isFromLoan
          ? "Total Loan Cost"
          : sipCalculation.calculationType === "interest" && isFromLoan
          ? "Loan Interest"
          : "SIP Target",
      value: sipTarget > 0 ? sipTarget : sipCalculation.totalSipInvestment || 0,
      color: sipCalculation.calculationType === "total" ? "var(--purple)"
           : sipCalculation.calculationType === "interest" ? "var(--danger)"
           : "var(--primary)",
    },
    { name: "SIP Returns", value: sipCalculation.sipReturns || 0, color: "var(--success)" },
  ];

  const chartTitle =
    sipCalculation.calculationType === "total" && isFromLoan
      ? "📊 Total Loan Cost vs SIP Returns"
      : sipCalculation.calculationType === "interest" && isFromLoan
      ? "📊 Loan Interest vs SIP Returns"
      : "📊 SIP Investment Analysis";

  return (
    <div>
      {/* Inputs + Results */}
      <div className="two-col-grid">

        {/* SIP Inputs */}
        <div className="card">
          <div className="card-accent-bar-green" />
          <h2 className="section-title gradient-title-green">📈 SIP Parameters</h2>

          <div className="input-group">
            <label htmlFor="sipTarget">Target Amount (₹)</label>
            <input
              id="sipTarget"
              type="number"
              value={sipTargetStr}
              onChange={(e) => { setSipTargetStr(e.target.value); setSipTarget(e.target.value === "" ? 0 : parseFloat(e.target.value) || 0); }}
              className="input-field"
              min="0"
              placeholder="e.g. 500000"
            />
            {sipTarget > 0 && (
              <div className="input-hint">Accumulate {formatCurrency(sipTarget)}</div>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="sipAmount">Monthly SIP Amount (₹)</label>
            <input
              id="sipAmount"
              type="number"
              value={sipAmountStr}
              onChange={(e) => { setSipAmountStr(e.target.value); setSipAmount(e.target.value === "" ? 0 : parseFloat(e.target.value) || 0); }}
              className="input-field"
              min="0"
              placeholder="0"
            />
          </div>

          <div className="input-group">
            <label htmlFor="sipPeriod">Investment Period (months)</label>
            <input
              id="sipPeriod"
              type="number"
              value={sipPeriodStr}
              onChange={(e) => { setSipPeriodStr(e.target.value); setSipPeriod(e.target.value === "" ? 0 : parseInt(e.target.value) || 0); }}
              className="input-field"
              min="1"
              placeholder="e.g. 120"
            />
            {sipPeriod > 0 && (
              <div className="input-hint">≈ {formatMonthsToYearsMonths(sipPeriod)}</div>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="sipReturnRate">Expected Annual Return (%)</label>
            <input
              id="sipReturnRate"
              type="number"
              value={sipReturnRateStr}
              onChange={(e) => { setSipReturnRateStr(e.target.value); setSipReturnRate(e.target.value === "" ? 0 : parseFloat(e.target.value) || 0); }}
              step="0.1"
              min="0.1"
              max="100"
              placeholder="e.g. 12"
              className="input-field"
            />
          </div>

          <div className="info-box">
            <h4>💡 How it works</h4>
            <p>
              Enter a monthly SIP and expected return to project wealth. Use the
              "SIP vs…" buttons on the Loan tab to auto-populate these fields
              for a side-by-side comparison.
            </p>
          </div>
        </div>

        {/* SIP Results */}
        <div className="card">
          <div className="card-accent-bar-green" />
          <h2 className="section-title gradient-title-green">💹 Results</h2>

          {isFromLoan && (
            <div
              className={`sip-target-banner ${sipCalculation.calculationType === "total" ? "blue" : "green"}`}
            >
              <div className="label">
                🎯 Comparing SIP against:{" "}
                {sipCalculation.calculationType === "total"
                  ? "Total Amount Payable"
                  : "Total Interest Only"}
              </div>
              <div className="amount">{formatCurrency(sipCalculation.targetAmount)}</div>
            </div>
          )}

          {sipTarget > 0 && (
            <ResultRow icon="🎯" label="Target Amount" value={formatCurrency(sipTarget)} color="var(--purple)" />
          )}
          <ResultRow
            icon="📅"
            label="Required Monthly SIP"
            value={formatCurrency(sipCalculation.requiredSip)}
            color="var(--primary)"
          />
          {sipAmount > 0 && sipAmount !== sipCalculation.requiredSip && (
            <div style={{ fontSize: 11, color: "var(--purple)", marginTop: -6, marginBottom: 6, fontStyle: "italic" }}>
              You set: {formatCurrency(sipAmount)}
            </div>
          )}
          <ResultRow
            icon="💵"
            label="Total Investment"
            value={formatCurrency(sipCalculation.totalSipInvestment)}
            color="var(--purple)"
          />
          <ResultRow
            icon="📈"
            label="SIP Returns"
            value={formatCurrency(sipCalculation.sipReturns)}
            color="var(--success)"
          />

          <div className="wealth-box">
            <div className="wealth-label">🏆 Net Wealth Created</div>
            <div className="wealth-value">{formatCurrency(sipCalculation.netBenefit)}</div>
            <div className="wealth-note">
              {sipCalculation.calculationType === "total" && isFromLoan
                ? "Wealth created instead of paying the total loan cost"
                : sipCalculation.calculationType === "interest" && isFromLoan
                ? "Wealth created instead of paying loan interest"
                : "Projected wealth from your SIP investment"}
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Charts */}
      <div className="chart-section">
        <h2 className="section-heading gradient-title-green">{chartTitle}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

          <div className="chart-wrapper">
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--gray-700)", marginBottom: 12, textAlign: "center" }}>
              {sipCalculation.calculationType === "total" && isFromLoan
                ? "Loan Total vs SIP Returns"
                : sipCalculation.calculationType === "interest" && isFromLoan
                ? "Loan Interest vs SIP Returns"
                : "Investment vs Returns"}
            </div>
            <ResponsiveContainer width="100%" height={270}>
              <PieChart>
                <Pie
                  data={comparisonData}
                  cx="50%"
                  cy="45%"
                  outerRadius={90}
                  innerRadius={45}
                  dataKey="value"
                  paddingAngle={3}
                  label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                  labelLine={false}
                >
                  {comparisonData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-wrapper">
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--gray-700)", marginBottom: 12, textAlign: "center" }}>
              SIP Growth Over Time
            </div>
            <ResponsiveContainer width="100%" height={270}>
              <ComposedChart data={sipCalculation.sipSchedule} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={axisStyle}
                  tickLine={false}
                  axisLine={false}
                  label={{ value: "Month", position: "insideBottom", offset: -12, fontSize: 11, fill: "var(--gray-400)" }}
                />
                <YAxis
                  tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                  tick={axisStyle}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip formatter={(v) => [formatCurrency(v)]} contentStyle={tooltipStyle} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                <Area type="monotone" dataKey="cumulativeInvestment" stackId="1" stroke="var(--purple)" fill={CHART_COLORS.sipInvestment} name="Investment" />
                <Area type="monotone" dataKey="growth" stackId="1" stroke="var(--success)" fill={CHART_COLORS.sipGrowth} name="Growth" />
                <Line type="monotone" dataKey="cumulativeReturns" stroke={CHART_COLORS.sipTotal} strokeWidth={2.5} name="Total Value" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* SIP Schedule Table */}
      <div className="table-section">
        <h2 className="section-heading gradient-title-green">📋 Investment Schedule</h2>
        <div className="table-container">
          <table className="schedule-table" aria-label="SIP investment schedule">
            <thead>
              <tr>
                {["Month", "SIP Amount", "Total Invested", "Total Value", "Growth"].map((h) => (
                  <th key={h} scope="col">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sipCalculation.sipSchedule.slice(0, 24).map((row) => (
                <tr key={row.month}>
                  <td style={{ fontWeight: 600, color: "var(--gray-400)" }}>{row.month}</td>
                  <td style={{ fontWeight: 600 }}>{formatCurrency(row.sipAmount)}</td>
                  <td style={{ color: "var(--purple)", fontWeight: 600 }}>{formatCurrency(row.cumulativeInvestment)}</td>
                  <td style={{ color: "var(--success)", fontWeight: 700 }}>{formatCurrency(row.cumulativeReturns)}</td>
                  <td style={{ color: "var(--success-hover)", fontWeight: 600 }}>{formatCurrency(row.growth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sipCalculation.sipSchedule.length > 24 && (
          <p style={{ textAlign: "center", fontSize: 13, color: "var(--gray-400)", marginTop: 12 }}>
            Showing first 24 months · Total period: {sipPeriod} months
          </p>
        )}
      </div>
    </div>
  );
};

export default SIPTab;
